/**
 * Challenge Mode Component
 * "Pick any ticker" instant prediction with reveal functionality
 */
import { useState, useCallback } from 'react';

interface Prediction {
  ticker: string;
  company: string;
  prediction: {
    fcf_value: number;
    confidence_interval: [number, number];
    direction: 'positive' | 'negative' | 'neutral';
    confidence: number;
  };
  hasActualData: boolean;
  historicalYearsAvailable: number[];
  responseTimeMs: number;
}

interface HistoricalFCF {
  year: number;
  fcf: number;
  source: string;
  filingDate: string;
}

interface RevealData {
  ticker: string;
  company: string;
  historicalFCF: HistoricalFCF[];
}

export function ChallengeMode() {
  const [ticker, setTicker] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [prediction, setPrediction] = useState<Prediction | null>(null);
  const [revealData, setRevealData] = useState<RevealData | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);

  const handlePredict = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTicker = ticker.trim().toUpperCase();

    if (!trimmedTicker) {
      setError('Please enter a ticker symbol');
      return;
    }

    setIsLoading(true);
    setError('');
    setPrediction(null);
    setRevealData(null);

    try {
      const response = await fetch('/api/challenge/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ticker: trimmedTicker }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Prediction failed');
        return;
      }

      setPrediction(data);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [ticker]);

  const handleReveal = useCallback(async () => {
    if (!prediction) return;

    setIsRevealing(true);

    try {
      const response = await fetch('/api/challenge/reveal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ticker: prediction.ticker }),
      });

      const data = await response.json();

      if (response.ok) {
        setRevealData(data);
      } else {
        setError(data.error || 'Failed to reveal actual data');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsRevealing(false);
    }
  }, [prediction]);

  const formatCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1e12) return `${sign}$${(absValue / 1e12).toFixed(2)}T`;
    if (absValue >= 1e9) return `${sign}$${(absValue / 1e9).toFixed(2)}B`;
    if (absValue >= 1e6) return `${sign}$${(absValue / 1e6).toFixed(2)}M`;
    return `${sign}$${absValue.toFixed(0)}`;
  };

  const handleReset = () => {
    setTicker('');
    setPrediction(null);
    setRevealData(null);
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="term-box p-4">
        <h2 className="text-term-accent font-bold text-lg mb-2">
          Challenge Mode
        </h2>
        <p className="text-term-textDim text-sm">
          Enter any public ticker to test our FCF prediction accuracy.
          See the prediction first, then reveal the actual historical data.
        </p>
      </div>

      {/* Input Form */}
      {!prediction && (
        <form onSubmit={handlePredict} className="term-box p-4">
          <label className="block text-term-textDim text-sm mb-2">
            <span className="text-term-green">$</span> Enter ticker symbol:
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              className="flex-1 bg-term-bgDark border border-term-border rounded px-3 py-2
                       text-term-text font-mono focus:outline-none focus:border-term-accent
                       placeholder-term-textDim uppercase"
              placeholder="AAPL, MSFT, GOOGL..."
              maxLength={5}
              disabled={isLoading}
              autoFocus
            />
            <button
              type="submit"
              disabled={isLoading || !ticker.trim()}
              className="bg-term-accent text-term-bgDark font-bold py-2 px-6 rounded
                       hover:bg-term-accentBright transition-colors disabled:opacity-50
                       disabled:cursor-not-allowed min-w-[140px]"
            >
              {isLoading ? 'Analyzing...' : 'Get Prediction'}
            </button>
          </div>

          {error && (
            <p className="text-term-red text-sm mt-3">
              <span className="text-term-red">✗</span> {error}
            </p>
          )}
        </form>
      )}

      {/* Prediction Result */}
      {prediction && !revealData && (
        <div className="term-box p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-term-text font-bold text-lg">
                {prediction.ticker}
              </h3>
              <p className="text-term-textDim text-sm">{prediction.company}</p>
            </div>
            <div className="text-right">
              <p className="text-term-textDim text-xs">Response time</p>
              <p className="text-term-green font-mono">
                {prediction.responseTimeMs}ms
              </p>
            </div>
          </div>

          <div className="bg-term-bgDark border border-term-border rounded p-4">
            <p className="text-term-textDim text-xs mb-1">FCF Prediction</p>
            <p className={`text-2xl font-bold ${
              prediction.prediction.direction === 'positive' ? 'text-term-green' :
              prediction.prediction.direction === 'negative' ? 'text-term-red' :
              'text-term-text'
            }`}>
              {formatCurrency(prediction.prediction.fcf_value)}
            </p>
            <p className="text-term-textDim text-xs mt-2">
              90% CI: {formatCurrency(prediction.prediction.confidence_interval[0])} —{' '}
              {formatCurrency(prediction.prediction.confidence_interval[1])}
            </p>
            <p className="text-term-textDim text-xs">
              Model Confidence: {(prediction.prediction.confidence * 100).toFixed(0)}%
            </p>
          </div>

          {prediction.hasActualData && (
            <div className="bg-term-bgMedium border border-term-yellow/30 rounded p-4">
              <p className="text-term-yellow text-sm mb-3">
                Historical data available for years: {prediction.historicalYearsAvailable.slice(0, 5).join(', ')}
              </p>
              <button
                onClick={handleReveal}
                disabled={isRevealing}
                className="w-full bg-term-yellow text-term-bgDark font-bold py-2 px-4 rounded
                         hover:bg-yellow-400 transition-colors disabled:opacity-50"
              >
                {isRevealing ? 'Loading...' : 'Reveal Actual FCF Data'}
              </button>
            </div>
          )}

          <button
            onClick={handleReset}
            className="w-full text-term-textDim text-sm hover:text-term-text transition-colors"
          >
            Try another ticker
          </button>
        </div>
      )}

      {/* Revealed Comparison */}
      {prediction && revealData && (
        <div className="term-box p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-term-text font-bold text-lg">
                {revealData.ticker} — Results
              </h3>
              <p className="text-term-textDim text-sm">{revealData.company}</p>
            </div>
          </div>

          {/* Side by Side Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-term-bgDark border border-term-accent rounded p-4">
              <p className="text-term-accent text-xs font-bold mb-2">OUR PREDICTION</p>
              <p className={`text-xl font-bold ${
                prediction.prediction.direction === 'positive' ? 'text-term-green' :
                prediction.prediction.direction === 'negative' ? 'text-term-red' :
                'text-term-text'
              }`}>
                {formatCurrency(prediction.prediction.fcf_value)}
              </p>
            </div>

            <div className="bg-term-bgDark border border-term-green rounded p-4">
              <p className="text-term-green text-xs font-bold mb-2">ACTUAL (Latest Year)</p>
              {revealData.historicalFCF.length > 0 ? (
                <p className="text-xl font-bold text-term-text">
                  {formatCurrency(revealData.historicalFCF[0].fcf)}
                </p>
              ) : (
                <p className="text-term-textDim">No data available</p>
              )}
            </div>
          </div>

          {/* Accuracy Calculation */}
          {revealData.historicalFCF.length > 0 && (
            <div className="bg-term-bgMedium border border-term-border rounded p-4">
              <p className="text-term-textDim text-xs mb-2">Accuracy Metrics</p>
              {(() => {
                const actual = revealData.historicalFCF[0].fcf;
                const predicted = prediction.prediction.fcf_value;
                const ape = Math.abs((predicted - actual) / actual) * 100;
                const directionCorrect =
                  (predicted > 0 && actual > 0) || (predicted < 0 && actual < 0);

                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-term-textDim text-xs">Absolute % Error</p>
                      <p className={`font-bold ${ape < 10 ? 'text-term-green' : ape < 25 ? 'text-term-yellow' : 'text-term-red'}`}>
                        {ape.toFixed(2)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-term-textDim text-xs">Direction</p>
                      <p className={directionCorrect ? 'text-term-green font-bold' : 'text-term-red font-bold'}>
                        {directionCorrect ? '✓ Correct' : '✗ Incorrect'}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Historical FCF Table */}
          {revealData.historicalFCF.length > 0 && (
            <div>
              <p className="text-term-textDim text-xs mb-2">Historical FCF (SEC 10-K Filings)</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-term-textDim text-left">
                    <th className="py-1">Year</th>
                    <th className="py-1 text-right">FCF</th>
                    <th className="py-1 text-right">Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {revealData.historicalFCF.map((h) => (
                    <tr key={h.year} className="border-t border-term-border">
                      <td className="py-2 text-term-text">{h.year}</td>
                      <td className="py-2 text-right font-mono text-term-text">
                        {formatCurrency(h.fcf)}
                      </td>
                      <td className="py-2 text-right text-term-textDim">
                        {h.filingDate}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            onClick={handleReset}
            className="w-full bg-term-accent text-term-bgDark font-bold py-2 px-4 rounded
                     hover:bg-term-accentBright transition-colors"
          >
            Try Another Ticker
          </button>
        </div>
      )}
    </div>
  );
}
